// src/marketing/docs/hooks/useMarketingDocs.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api'
import type { MarketingDoc } from '../../../lib/types'

export type CreateMarketingDocInput = {
  appId: string
  slug: string
  title: string
  sortOrder: number
  bodyMd?: string
}

export type UpdateMarketingDocInput = {
  title?: string
  sortOrder?: number
  bodyMd?: string
}

const MARKETING_DOC_KEYS = {
  all: ['marketing-docs'] as const,
  list: (appId: string) => ['marketing-docs', 'list', appId] as const,
  detail: (id: string) => ['marketing-docs', 'detail', id] as const,
}

export function useMarketingDocs(appId: string | null) {
  return useQuery<MarketingDoc[]>({
    queryKey: MARKETING_DOC_KEYS.list(appId ?? ''),
    queryFn: () =>
      api
        .get<{ docs: MarketingDoc[] }>(`/api/marketing-docs?appId=${appId}`)
        .then((r) => r.docs),
    enabled: appId !== null,
    staleTime: 1000 * 60 * 2,
  })
}

export function useMarketingDoc(id: string | null) {
  return useQuery<MarketingDoc>({
    queryKey: MARKETING_DOC_KEYS.detail(id ?? ''),
    queryFn: () =>
      api
        .get<{ doc: MarketingDoc }>(`/api/marketing-docs/${id}`)
        .then((r) => r.doc),
    enabled: id !== null,
    staleTime: 1000 * 60 * 2,
  })
}

export function useCreateMarketingDoc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateMarketingDocInput) =>
      api
        .post<{ doc: MarketingDoc }>('/api/marketing-docs', data)
        .then((r) => r.doc),
    onSuccess: (doc) => {
      void qc.invalidateQueries({ queryKey: MARKETING_DOC_KEYS.list(doc.appId) })
    },
  })
}

export type UpdateMarketingDocVariables = {
  id: string
  data: UpdateMarketingDocInput
}

export function useUpdateMarketingDoc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: UpdateMarketingDocVariables) =>
      api
        .put<{ doc: MarketingDoc }>(`/api/marketing-docs/${id}`, data)
        .then((r) => r.doc),
    onSuccess: (doc) => {
      void qc.invalidateQueries({ queryKey: MARKETING_DOC_KEYS.all })
      void qc.invalidateQueries({ queryKey: MARKETING_DOC_KEYS.detail(doc.id) })
    },
  })
}

export function useDeleteMarketingDoc() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id: string) =>
      api.del<{ success: boolean }>(`/api/marketing-docs/${id}`).then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MARKETING_DOC_KEYS.all })
    },
  })
}
